import clsx from 'clsx'
import { AlertTriangleIcon, ArrowDownRightIcon, ArrowUpRightIcon, StarIcon } from 'lucide-react'

import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { type Period, type RenewalApproach } from 'apps/employer_renewals/types'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Badge, SolidAlertTriangleIcon, useBreakpoint } from 'slab'
import { formatMoney } from 'utils'

import BigMoney from './BigMoney'
import { getRenewalApproachHighlights } from './RenewalApproachHighlights'

export type RenewalCostsProps = {
  currentYearCost: number
  renewalYearCost: number
  renewalApproach: RenewalApproach
  period: Period
  qmeEnabled?: boolean
  isLoading: boolean
}

export default function RenewalCosts ({
  currentYearCost,
  renewalYearCost,
  renewalApproach,
  period,
  qmeEnabled,
  isLoading,
}: RenewalCostsProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { isUsingFlatAllowances } = useEmployerRenewalConfig()
  const { employeeFilter } = useAllowances()

  const isIncrease = renewalYearCost > currentYearCost
  const isUnchanged = renewalYearCost === currentYearCost
  const percentageChange = ((renewalYearCost - currentYearCost) / (currentYearCost || 1)) * 100
  const formattedCurrentYearCost = formatMoney(currentYearCost)
  const currentYearCostDollars = formattedCurrentYearCost.split('.')[0]

  const highlights = getRenewalApproachHighlights(renewalApproach, isUsingFlatAllowances, qmeEnabled)

  if (renewalApproach === 'reduce') {
    return (
      <DecreasedCosts
        renewalYearCost={renewalYearCost}
        period={period}
        isLoading={isLoading}
        highlights={highlights}
      />
    )
  }

  return (
    <div className='flex flex-row border border-gray-400 rounded-2xl p-6 gap-6 flex-wrap'>
      <div className='flex flex-col flex-1 gap-4'>
        {isLoading
          ? <SkeletonItem heightClass='h-8' widthClass='w-36' />
          : <BigMoney money={renewalYearCost} period={period} />}
        <div>
          {isLoading
            ? <SkeletonItem heightClass='h-4' widthClass='w-24' />
            : (
              <Badge leftIcon={isUnchanged ? undefined : isIncrease ? ArrowUpRightIcon : ArrowDownRightIcon}>
                {isUnchanged
                  ? 'Unchanged from last year'
                  : (
                    <>
                      {isIncrease ? '+' : ''} {percentageChange.toFixed(0)}% from {currentYearCostDollars}{period === 'monthly' ? '/mo' : ''} last year
                    </>
                    )}
              </Badge>
              )}
        </div>
      </div>
      <div className={clsx('bg-gray-200', {
        'w-[1px]': breakpointIsAtLeast('xs'),
        'h-[1px] w-full': !breakpointIsAtLeast('xs'),
      })}
      />
      <div className='flex flex-col justify-center flex-1 gap-4'>
        <div className='flex flex-col gap-2'>
          {highlights.map((highlight) => (
            isLoading
              ? <SkeletonItem key={highlight} heightClass='h-4' widthClass='w-full' />
              : (
                <div key={highlight} className='flex flex-row gap-2 items-center'>
                  <StarIcon className='w-[14px] h-[14px] text-thatch-500' />
                  <span className='body-sm flex-1'>{highlight}</span>
                </div>
                )
          ))}
          <span className='body-sm text-gray-500'>
            {employeeFilter === 'eligible' ? 'Assumes all eligible employees will enroll for 2026 benefits' : 'Assumes all employees enrolled in 2025 will re-enroll for 2026 benefits'}
          </span>
        </div>
      </div>
    </div>
  )
}

function DecreasedCosts ({
  renewalYearCost,
  period,
  isLoading,
  highlights,
}: Omit<RenewalCostsProps, 'currentYearCost' | 'renewalApproach'> & { highlights: string[] }) {
  const { employeeFilter } = useAllowances()

  return (
    <div className='flex flex-col border border-orange-300 rounded-2xl overflow-hidden'>
      <div className='flex flex-row gap-2 items-center justify-between p-6 flex-wrap bg-orange-50'>
        {isLoading
          ? <SkeletonItem heightClass='h-8' widthClass='w-36' />
          : <BigMoney money={renewalYearCost} period={period} />}
        <div className='flex flex-row gap-2 items-center px-2 py-1 rounded-lg bg-orange-200'>
          <SolidAlertTriangleIcon className='w-3 h-3 text-orange-500 shrink-0' />
          <div className='body-sm-bold text-orange-500'>Reducing benefits puts employees' financial security at risk</div>
        </div>
      </div>
      <div className='flex flex-col justify-center flex-1 gap-4 p-6'>
        <div className='flex flex-col gap-2'>
          {highlights.map((highlight) => (
            <div key={highlight} className='flex flex-row gap-2 items-center'>
              <AlertTriangleIcon className='w-[14px] h-[14px] text-orange-400' />
              <span className='body-sm flex-1'>{highlight}</span>
            </div>
          ))}
          <span className='body-sm text-gray-500'>
            {employeeFilter === 'eligible' ? 'Assumes all eligible employees will enroll for 2026 benefits' : 'Assumes all employees enrolled in 2025 will re-enroll for 2026 benefits'}
          </span>
        </div>
      </div>
    </div>
  )
}
